import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import logo from '../../assets/img/logo/logo.svg';
import { Endpoints, mail } from '../../constants';
import smartHttp from '../../helpers/smartHttp';
import { Notify } from '../Alert/Notify';
import { GenericError, GenericSuccess } from '../../constants';
import Loader from '../../components/Loader';
import { getDbConfigData } from '../../helpers/component-helper-methods';
import { useDispatch } from 'react-redux';
import CommonDropdown from '../CommonDropdown/CommonDropdown';
import DistributorsDropdown from '../../pages/Entitlements/DistributorsDropdown';
import TextAreaInput from '../TextAreaInput/TextAreaInput';
import store from '../../redux/store';
import './FeedbackForm.scss';

const FeedbackForm = ({
  UserGuidesTrainings,
  filteredDistributors,
  callBackDistributors,
  visible,
  setVisible,
}) => {
  const [loading, setLoading] = useState(false);
  const [feedbackCollection, setFeedbackCollection] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState(null);
  const [selectedDistributors, setSelectedDistributors] = useState([]);
  const dispatch = useDispatch();

  const initialValues = {
    feedbackType: '',
    feedback: '',
  };

  const feedbackValidationSchema = Yup.object({
    feedbackType: Yup.string().required('Please select feedback type'),
    feedback: Yup.string()
      .min(5, 'Feedback must be at least 5 characters')
      .max(200, 'Feedback must be at most 200 characters')
      .required('Feedback is required'),
  });

  const trainingValidationSchema = Yup.object({
    feedback: Yup.string().max(
      200,
      'Additional comments must be at most 200 characters',
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues,
    validationSchema: UserGuidesTrainings
      ? trainingValidationSchema
      : feedbackValidationSchema,
    onSubmit: (values) => {
      // For training requests, use selectedDistributors state instead of formik value
      const feedbackTypeToSend = UserGuidesTrainings
        ? selectedDistributors
        : values.feedbackType;
      sendEmailToCISupport(feedbackTypeToSend, values.feedback);
    },
  });

  const getFeedbackDataAndTemplates = async () => {
    try {
      let storeDBConfig = await getDbConfigData(dispatch);

      // Set feedback collection
      if (storeDBConfig?.feedbackCollection?.feedback_nature?.length > 0) {
        const formattedData =
          storeDBConfig.feedbackCollection.feedback_nature.map((item) => ({
            label: item,
            value: item,
          }));
        setFeedbackCollection(formattedData);
      } else {
        setFeedbackCollection([]);
      }

      // Set email templates
      if (storeDBConfig?.emailTemplates) {
        setEmailTemplates(storeDBConfig.emailTemplates);
      }
    } catch (err) {
      setFeedbackCollection([]);
    }
  };

  useEffect(() => {
    getFeedbackDataAndTemplates();
  }, []);

  const getEmailTemplateDetails = (isTrainingRequest) => {
    if (!emailTemplates) {
      return null;
    }
    return isTrainingRequest
      ? emailTemplates.training
      : emailTemplates.feedback;
  };

  const getAccountName = async (userEmail) => {
    let accountName = 'N/A';

    try {
      if (userEmail) {
        const queryParams = {
          Filter: `email:"${userEmail}" and contractNumber!=""`,
        };

        const userResponse = await smartHttp.get(
          Endpoints.userApi,
          queryParams,
        );

        if (userResponse.data?.results?.length > 0) {
          const userData = userResponse.data.results[0];
          accountName = userData.account || 'N/A';
        }
      }
    } catch (err) {}

    return accountName;
  };

  const formatDeliveryPlatforms = (feedbackType) => {
    if (!Array.isArray(feedbackType) || feedbackType.length === 0) {
      return typeof feedbackType === 'string' ? feedbackType : 'N/A';
    }

    const distributorNames = feedbackType
      .map(
        (dist) =>
          dist.distributorLabel ||
          dist.distributorDisplayName ||
          dist.distributorPlatformDisplayName ||
          dist.distributorName ||
          dist.distributor ||
          dist.label ||
          dist.value ||
          '',
      )
      .filter((name) => name);

    // Format as HTML list for better readability when many items
    if (distributorNames.length > 1) {
      return (
        '<ul style="margin: 5px 0; padding-left: 20px;">' +
        distributorNames.map((name) => `<li>${name}</li>`).join('') +
        '</ul>'
      );
    }

    // For 1 or fewer, use comma-separated
    return distributorNames.join(', ');
  };

  const sendEmailToCISupport = async (feedbackType, feedback) => {
    try {
      setLoading(true);

      // Determine if this is a training request or feedback
      const isTrainingRequest = UserGuidesTrainings;

      // Validate delivery platform selection for training requests
      if (isTrainingRequest && (!feedbackType || feedbackType.length === 0)) {
        return;
      }

      // Get email template from state based on form type
      const emailTemplate = getEmailTemplateDetails(isTrainingRequest);

      if (!emailTemplate) {
        const templateType = isTrainingRequest
          ? 'Training Request'
          : 'Feedback';
        Notify({
          alert: true,
          type: 'error',
          title: `${templateType} email template not found. Please contact administrator.`,
        });
        setLoading(false);
        return;
      }

      // Get user information from Redux store
      const storeData = store.getState();
      const userInfo = storeData?.userInfo?.userInfo;
      const userEmail = userInfo?.email || '';
      const contactName =
        userInfo?.contactName ||
        userInfo.FirstName + ' ' + userInfo.LastName ||
        '';
      const submittedBy =
        contactName && userEmail
          ? `${contactName} (${userEmail})`
          : contactName || userEmail || 'Unknown User';

      // Get account name using helper function
      const accountName = await getAccountName(userEmail);

      // Format delivery platforms using helper function
      const formattedFeedbackType = formatDeliveryPlatforms(feedbackType);

      // Replace placeholders in email template with actual values
      const replacePlaceholders = (text) => {
        return text
          .replaceAll('$Account_Name$', accountName)
          .replaceAll('$Feedback_Type$', formattedFeedbackType)
          .replaceAll('$Delivery_Platforms$', formattedFeedbackType)
          .replaceAll('$Submitted_By$', submittedBy)
          .replaceAll('$Feedback_Text$', feedback || '')
          .replaceAll('$Training_Request_Text$', feedback || '');
      };

      const emailData = {
        MAIL_FROM_ADDRESS: mail.mail_from_address,
        MAIL_TO_ADDRESS: emailTemplate.MAIL_TO_ADDRESS || mail.mail_to_address,
        MAIL_CC_ADDRESS: emailTemplate.MAIL_CC_ADDRESS || userEmail,
        MAIL_BODY: replacePlaceholders(emailTemplate.MAIL_BODY),
        MAIL_SUBJECT: replacePlaceholders(emailTemplate.MAIL_SUBJECT),
      };

      // Send email
      const response = await smartHttp.post(
        Endpoints.sendEmailApi,
        btoa(JSON.stringify(emailData)),
      );

      const isSuccess = response.status === 200;
      const successMessage = isTrainingRequest
        ? GenericSuccess.trainingRequestSubmitted
        : GenericSuccess.feedbackSubmitted;

      // Close dialog and reset form
      setLoading(false);
      setVisible(false);
      formik.resetForm();

      // Show notification after dialog closes
      setTimeout(() => {
        Notify({
          alert: true,
          type: isSuccess ? 'success' : 'error',
          title: isSuccess ? successMessage : GenericError.somethingWentWrong,
        });
      }, 300);
    } catch (err) {
      setLoading(false);
      setVisible(false);

      setTimeout(() => {
        Notify({
          alert: true,
          type: 'error',
          title: GenericError.somethingWentWrong,
        });
      }, 300);
    }
  };

  const handleClose = () => {
    setVisible(false);
    formik.resetForm();
  };

  return (
    <>
      <Dialog
        header={<img src={logo} alt="Logo" />}
        visible={visible}
        position="bottom-right"
        className="feedback-form-dialog"
        onHide={handleClose}
        draggable={false}
        resizable={false}
        focusOnShow={false}
        dismissableMask={true}
      >
        {loading && (
          <div className="feedback-form-loader-overlay">
            <Loader type="scaleLoader" />
          </div>
        )}
        <form onSubmit={formik.handleSubmit} className="feedback-form">
          {UserGuidesTrainings ? (
            <div className="col mb-3">
              <DistributorsDropdown
                DataSet={filteredDistributors}
                callBackDistributors={(selectedDistributor) => {
                  // Store selected distributors in state
                  setSelectedDistributors(selectedDistributor);
                  // Call the original callback if provided
                  if (callBackDistributors) {
                    callBackDistributors(selectedDistributor);
                  }
                }}
              />
            </div>
          ) : (
            <div className="mb-3">
              <CommonDropdown
                mode="single"
                enableSearch={true}
                formLabel={'What is the nature of your feedback?'}
                name="feedbackType"
                options={feedbackCollection}
                formik={{ values: formik.values, errors: {}, touched: {} }}
                ddPlaceHolder={
                  formik.values.feedbackType !== '' &&
                  feedbackCollection?.length > 0
                    ? feedbackCollection.find(
                        (item) => item.value === formik.values.feedbackType,
                      )?.label || 'Select'
                    : 'Select'
                }
                labelKeyName="label"
                ddName="feedbackType"
                onChangeHandler={(option) => {
                  formik.setFieldValue('feedbackType', option.value);
                }}
                addAsterisk
              />
              {formik.errors.feedbackType && formik.touched.feedbackType && (
                <div className="form-input-error">
                  {formik.errors.feedbackType}
                </div>
              )}
            </div>
          )}
          <TextAreaInput
            formLabel={
              UserGuidesTrainings
                ? 'Additional Comments'
                : 'Share your feedback below'
            }
            id="feedback"
            name="feedback"
            {...formik.getFieldProps('feedback')}
            formik={formik}
            rows={6}
            onBlur={(e) => {
              formik.handleBlur(e);
              formik.setFieldTouched('feedback', true);
              formik.validateField('feedback');
            }}
            formGroupClass="custom-dropdown"
            addAsterisk={!UserGuidesTrainings}
          />

          <button
            type="submit"
            className="user-save-btn btn btn-dark float-end btn-opacity mt-2"
            disabled={
              formik.isSubmitting ||
              loading ||
              (UserGuidesTrainings && selectedDistributors.length === 0)
            }
          >
            {UserGuidesTrainings ? 'Submit Training Request' : 'Submit'}
          </button>
        </form>
      </Dialog>
    </>
  );
};

export default FeedbackForm;
